<template>
  <div class="p-6 space-y-8">
    
    <!-- Welcome Header -->
    <div class="flex justify-between items-center">
      <div>
        <h1 class="text-3xl font-bold text-slate-800 dark:text-white">Snowflake Command Center</h1>
        <p class="text-slate-500 mt-1">System Health, Cost, and Performance Overview</p>
      </div>
      <div class="flex items-center space-x-2 text-sm text-slate-500">
        <span class="relative flex h-3 w-3">
          <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
          <span class="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
        </span>
        <span class="font-medium">System Operational</span>
      </div>
    </div>
    
    <div v-if="loading" class="flex justify-center py-20">
       <div class="animate-spin h-10 w-10 border-4 border-indigo-500 border-t-transparent rounded-full"></div>
    </div>

    <div v-else class="space-y-12">
      <!-- Section 1: System Health & Performance -->
      <section class="space-y-6">
        <div class="flex items-center gap-2 mb-4">
          <div class="h-8 w-1 bg-green-500 rounded-full"></div>
          <h2 class="text-xl font-bold text-slate-800 dark:text-white">System Health & Performance</h2>
        </div>
        
        <!-- KPI Row -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <KPICard 
             title="Connection Status" 
             :value="healthData.status.status" 
             :subtext="healthData.status.latency_ms + ' ms latency'"
             icon-name="heroicons:server-stack"
             icon-color="text-green-500"
             bg-color="bg-green-50 dark:bg-green-900/10"
          />
          <KPICard 
             title="Credits Used (30d)" 
             :value="healthData.credits.total_30d" 
             :subtext="formatCurrency(healthData.credits.estimated_cost_usd)"
             icon-name="heroicons:banknotes"
             icon-color="text-indigo-500"
             bg-color="bg-indigo-50 dark:bg-indigo-900/10"
          />
          <KPICard 
             title="Active Queries" 
             :value="healthData.queries.active_count" 
             subtext="14k Total Today"
             icon-name="heroicons:bolt"
             icon-color="text-amber-500"
             bg-color="bg-amber-50 dark:bg-amber-900/10"
          />
          <KPICard 
             title="Security Alerts" 
             :value="healthData.security.critical_alerts" 
             subtext="3 Failed Logins"
             icon-name="heroicons:shield-exclamation"
             icon-color="text-red-500"
             bg-color="bg-red-50 dark:bg-red-900/10"
          />
        </div>

        <!-- Main Content Grid -->
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <!-- Spend Chart (2/3 width) -->
          <div class="lg:col-span-2 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
              <h3 class="text-lg font-semibold text-slate-800 dark:text-white mb-6">Warehouse Spend (Last 6 Months)</h3>
              <!-- <DashboardSpendChart :data="healthData.credits.history_6m" /> -->
          </div>

          <!-- Hotspots / Long Queries (1/3 width) -->
          <div class="lg:col-span-1 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6 flex flex-col">
              <h3 class="text-lg font-semibold text-slate-800 dark:text-white mb-4">Long Running Queries</h3>
              <div class="flex-1 overflow-auto space-y-4 pr-2 custom-scrollbar">
                  <div v-for="query in healthData.queries.long_running" :key="query.query_id" class="p-3 rounded-lg bg-slate-50 dark:bg-slate-700/50 border border-slate-100 dark:border-slate-600">
                      <div class="flex justify-between items-start mb-1">
                          <span class="text-xs font-mono text-slate-500">{{ query.query_id.substring(0,8) }}...</span>
                          <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                              {{ query.duration_sec }}s
                          </span>
                      </div>
                      <div class="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 flex items-center">
                          <Icon name="heroicons:user" class="w-3 h-3 mr-1 text-slate-400" />
                          {{ query.user_name }}
                      </div>
                      <p class="text-xs text-slate-500 font-mono truncate">
                          {{ query.query_text }}
                      </p>
                  </div>
              </div>
              <button class="mt-4 w-full py-2 text-sm text-indigo-600 dark:text-indigo-400 font-medium hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors">
                  View All Queries
              </button>
          </div>
        </div>
      </section>

      <div class="border-t border-slate-200 dark:border-slate-700 my-8 opacity-50"></div>

      <!-- Section 2: Cost Analytics -->
      <section class="space-y-8">
        <div class="flex items-center justify-between mb-2">
          <div class="flex items-center gap-2">
            <div class="h-8 w-1 bg-purple-500 rounded-full"></div>
            <h2 class="text-2xl font-bold text-slate-800 dark:text-white">Cost Analytics</h2>
          </div>
          <div class="flex items-center space-x-2 text-sm text-slate-500">
            <Icon name="heroicons:clock" class="w-4 h-4" />
            <span>Updated 5m ago</span>
          </div>
        </div>

        <!-- Cost Overview Cards -->
        <CostOverviewCards v-if="overviewData" :data="overviewData" />

        <!-- Cost Trend & Warehouse Breakdown -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <CostTrendChart v-if="trendData" :data="trendData.data" />
          <CostByWarehouse v-if="warehouseData" :warehouses="warehouseData.warehouses" />
        </div>

        <!-- Recommendations & Anomalies -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <CostRecommendations 
            v-if="recommendationsData" 
            :recommendations="recommendationsData.recommendations"
            @action="handleRecAction"
          />
          <CostAnomalies 
            v-if="anomaliesData" 
            :anomalies="anomaliesData.anomalies"
            @investigate="handleAnomInvestigate"
            @dismiss="handleAnomDismiss"
          />
        </div>

        <!-- Usage Patterns (Heatmap) -->
        <div v-if="heatmapData" class="space-y-4">
          <div class="flex items-center gap-2">
            <Icon name="heroicons:calendar" class="w-5 h-5 text-purple-500" />
            <h3 class="text-lg font-semibold text-slate-800 dark:text-white">Usage Patterns</h3>
          </div>
          <CostHeatmap 
            :data="heatmapData.data"
            @cell-click="handleHeatmapClick"
          />
        </div>

        <!-- User Spend Table -->
        <div v-if="userData" class="space-y-4">
          <div class="flex items-center gap-2">
            <Icon name="heroicons:users" class="w-5 h-5 text-purple-500" />
            <h3 class="text-lg font-semibold text-slate-800 dark:text-white">User Spend Breakdown</h3>
          </div>
          <CostByUserTable 
            :users="userData.users"
            @user-click="handleUserClick"
          />
        </div>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import KPICard from '~/components/dashboard/KPICard.vue'
import CostOverviewCards from '~/components/cost/CostOverviewCards.vue'
import CostTrendChart from '~/components/cost/CostTrendChart.vue'
import CostByWarehouse from '~/components/cost/CostByWarehouse.vue'
import CostByUserTable from '~/components/cost/CostByUserTable.vue'
import CostAnomalies from '~/components/cost/CostAnomalies.vue'
import CostRecommendations from '~/components/cost/CostRecommendations.vue'
import CostHeatmap from '~/components/cost/CostHeatmap.vue'

const loading = ref(true)
const healthData = ref<any>(null)

// Cost Analytics Data
const { 
  fetchOverview, 
  fetchTrend, 
  fetchByWarehouse, 
  fetchByUser, 
  fetchAnomalies, 
  fetchRecommendations,
  fetchHeatmap
} = useCostData()

const overviewData = ref<any>(null)
const trendData = ref<any>(null)
const warehouseData = ref<any>(null)
const userData = ref<any>(null)
const anomaliesData = ref<any>(null)
const recommendationsData = ref<any>(null)
const heatmapData = ref<any>(null)

const fetchData = async () => {
    try {
        const [health, overview, trend, warehouse, user, anomalies, recommendations, heatmap] = await Promise.all([
          $fetch('/api/mocks/snowflake/health'),
          fetchOverview(),
          fetchTrend(),
          fetchByWarehouse(),
          fetchByUser(),
          fetchAnomalies(),
          fetchRecommendations(),
          fetchHeatmap()
        ])
        
        healthData.value = health
        overviewData.value = overview
        trendData.value = trend
        warehouseData.value = warehouse
        userData.value = user
        anomaliesData.value = anomalies
        recommendationsData.value = recommendations
        heatmapData.value = heatmap
        
        loading.value = false
    } catch (error) {
        console.error('Error fetching dashboard data:', error)
    }
}

fetchData()

const formatCurrency = (val: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val)
}

// Event handlers for cost components
const handleAnomInvestigate = (anomaly: any) => console.log('Investigating anomaly:', anomaly)
const handleAnomDismiss = (anomalyId: string) => {
  if (anomaliesData.value) {
    anomaliesData.value.anomalies = anomaliesData.value.anomalies.filter((a: any) => a.id !== anomalyId)
  }
}
const handleRecAction = (recommendation: any) => alert(`Action: ${recommendation.action}\nPotential Savings: $${recommendation.potentialSavings}`)
const handleUserClick = (user: any) => console.log('User clicked:', user)
const handleHeatmapClick = (cellData: any) => console.log('Heatmap cell clicked:', cellData)
</script>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 4px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: rgba(156, 163, 175, 0.5);
  border-radius: 20px;
}
</style>
